import React, { useState, useEffect } from "react";
import axios from "axios";
import { URL } from "../AxiosUrlGlobal";
import "./PaymentErrors.css";

function PaymentErrors() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalLogs: 0,
    limit: 50,
  });
  const [stats, setStats] = useState({
    total: 0,
    resolved: 0,
    unresolved: 0,
    bkash: 0,
    balance: 0,
    wallet: 0,
    android: 0,
    ios: 0,
    web: 0,
  });
  const [paymentTypesBreakdown, setPaymentTypesBreakdown] = useState([]);
  const [stageBreakdown, setStageBreakdown] = useState([]);
  const [recentErrors, setRecentErrors] = useState([]);
  const [filters, setFilters] = useState({
    page: 1,
    limit: 50,
    platform: "all",
    errorType: "all",
    resolved: "all",
    search: "",
    dateFrom: "",
    dateTo: "",
    sortBy: "createdAt",
    sortOrder: "desc",
  });
  const [selectedLog, setSelectedLog] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [resolveNotes, setResolveNotes] = useState("");
  const [resolving, setResolving] = useState(false);

  useEffect(() => {
    fetchLogs();
  }, [
    filters.page,
    filters.limit,
    filters.platform,
    filters.errorType,
    filters.resolved,
    filters.sortOrder,
  ]);

  const fetchLogs = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      const uid = localStorage.getItem("id");

      const params = { ...filters };
      Object.keys(params).forEach((key) => {
        if (params[key] === "" || params[key] === "all") {
          delete params[key];
        }
      });

      const response = await axios.post(
        `${URL}/payment-error-logs`,
        params,
        {
          headers: { token, uid },
        }
      );

      if (response.data.success && response.data.data) {
        setLogs(response.data.data.logs);
        setPagination(response.data.data.pagination);
        setStats(response.data.data.stats);
        setPaymentTypesBreakdown(response.data.data.paymentTypesBreakdown || []);
        setStageBreakdown(response.data.data.stageBreakdown || []);
        setRecentErrors(response.data.data.recentErrors || []);
      }
    } catch (error) {
      console.error("Error fetching payment error logs:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
      page: key !== "page" ? 1 : value,
    }));
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setFilters((prev) => ({ ...prev, page: 1 }));
    fetchLogs();
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      handleFilterChange("page", newPage);
    }
  };

  const openDetailModal = (log) => {
    setSelectedLog(log);
    setResolveNotes("");
    setShowDetailModal(true);
  };

  const closeDetailModal = () => {
    setShowDetailModal(false);
    setSelectedLog(null);
    setResolveNotes("");
  };

  const handleResolve = async () => {
    if (!selectedLog) return;

    try {
      setResolving(true);
      const token = localStorage.getItem("token");
      const uid = localStorage.getItem("id");

      const response = await axios.put(
        `${URL}/payment-error-logs/resolve/${selectedLog._id}`,
        { notes: resolveNotes },
        { headers: { token, uid } }
      );

      if (response.data.success) {
        setLogs((prev) =>
          prev.map((log) =>
            log._id === selectedLog._id
              ? { ...log, crashData: { ...log.crashData, resolved: true, resolutionNotes: resolveNotes } }
              : log
          )
        );
        closeDetailModal();
        fetchLogs();
      }
    } catch (error) {
      console.error("Error resolving payment error:", error);
    } finally {
      setResolving(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      timeZone: "Asia/Dhaka",
    });
  };

  const getPlatformIcon = (platform) => {
    const icons = {
      android: "android",
      ios: "phone_iphone",
      web: "language",
    };
    return icons[platform] || "devices";
  };

  const getPaymentTypeColor = (paymentType) => {
    const colors = {
      bkash_error: "#e2136e",
      balance_error: "#ff9800",
      wallet_error: "#9c27b0",
    };
    return colors[paymentType] || "#95a5a6";
  };

  const getPaymentTypeLabel = (paymentType) => {
    const labels = {
      bkash_error: "bKash",
      balance_error: "Balance",
      wallet_error: "Wallet",
    };
    return labels[paymentType] || paymentType;
  };

  const getStageLabel = (stage) => {
    const labels = {
      create: "Create Payment",
      execute: "Execute Payment",
      callback: "Callback",
      save: "Save Payment",
      deduct: "Deduct Balance",
      process: "Process",
      init: "Initialize",
    };
    return labels[stage] || stage;
  };

  const formatAmount = (amount) => {
    if (!amount) return "-";
    return `৳${amount.toLocaleString()}`;
  };

  return (
    <div className="pe-container">
      {/* Header */}
      <div className="pe-header">
        <h1>Payment Errors</h1>
        <p>Monitor and resolve payment failures</p>
      </div>

      {/* Stats */}
      <div className="pe-stats">
        <div className="pe-stat pe-stat-total">
          <span className="material-symbols-outlined">payments</span>
          <div>
            <small>Total</small>
            <strong>{stats.total}</strong>
          </div>
        </div>
        <div className="pe-stat pe-stat-unresolved">
          <span className="material-symbols-outlined">error</span>
          <div>
            <small>Unresolved</small>
            <strong>{stats.unresolved}</strong>
          </div>
        </div>
        <div className="pe-stat pe-stat-resolved">
          <span className="material-symbols-outlined">check_circle</span>
          <div>
            <small>Resolved</small>
            <strong>{stats.resolved}</strong>
          </div>
        </div>
        <div className="pe-stat pe-stat-bkash">
          <span className="material-symbols-outlined">account_balance_wallet</span>
          <div>
            <small>bKash</small>
            <strong>{stats.bkash}</strong>
          </div>
        </div>
        <div className="pe-stat pe-stat-balance">
          <span className="material-symbols-outlined">account_balance</span>
          <div>
            <small>Balance</small>
            <strong>{stats.balance}</strong>
          </div>
        </div>
        <div className="pe-stat pe-stat-android">
          <span className="material-symbols-outlined">android</span>
          <div>
            <small>Android</small>
            <strong>{stats.android}</strong>
          </div>
        </div>
        <div className="pe-stat pe-stat-ios">
          <span className="material-symbols-outlined">phone_iphone</span>
          <div>
            <small>iOS</small>
            <strong>{stats.ios}</strong>
          </div>
        </div>
      </div>

      {/* Recent Errors Summary */}
      {recentErrors.length > 0 && (
        <div className="pe-recent-errors">
          <h3>Top Unresolved Payment Errors</h3>
          <div className="pe-error-list">
            {recentErrors.slice(0, 5).map((error, index) => (
              <div key={index} className="pe-error-item">
                <span className="pe-error-count">{error.count}x</span>
                <span className="pe-error-message">{error._id?.substring(0, 60) || "Unknown error"}...</span>
                <span className="pe-error-type" style={{ backgroundColor: getPaymentTypeColor(error.paymentType) }}>
                  {getPaymentTypeLabel(error.paymentType)}
                </span>
                <span className="pe-error-amount">{formatAmount(error.totalAmount)}</span>
                <span className={`pe-error-platform ${error.platform}`}>
                  <span className="material-symbols-outlined">{getPlatformIcon(error.platform)}</span>
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="pe-filters">
        <form onSubmit={handleSearch}>
          <div className="pe-filter-row">
            <select
              value={filters.platform}
              onChange={(e) => handleFilterChange("platform", e.target.value)}
            >
              <option value="all">All Platforms</option>
              <option value="android">Android</option>
              <option value="ios">iOS</option>
              <option value="web">Web</option>
            </select>

            <select
              value={filters.errorType}
              onChange={(e) => handleFilterChange("errorType", e.target.value)}
            >
              <option value="all">All Payment Types</option>
              <option value="bkash_error">bKash</option>
              <option value="balance_error">Balance</option>
              <option value="wallet_error">Wallet</option>
            </select>

            <select
              value={filters.resolved}
              onChange={(e) => handleFilterChange("resolved", e.target.value)}
            >
              <option value="all">All Status</option>
              <option value="false">Unresolved</option>
              <option value="true">Resolved</option>
            </select>

            <select
              value={filters.sortOrder}
              onChange={(e) => handleFilterChange("sortOrder", e.target.value)}
            >
              <option value="desc">Newest</option>
              <option value="asc">Oldest</option>
            </select>

            <input
              type="date"
              value={filters.dateFrom}
              onChange={(e) => handleFilterChange("dateFrom", e.target.value)}
            />

            <input
              type="date"
              value={filters.dateTo}
              onChange={(e) => handleFilterChange("dateTo", e.target.value)}
            />

            <input
              type="text"
              value={filters.search}
              onChange={(e) => setFilters((prev) => ({ ...prev, search: e.target.value }))}
              placeholder="Search errors, payment IDs..."
            />

            <button type="submit">
              <span className="material-symbols-outlined">search</span>
            </button>
          </div>
        </form>
      </div>

      {/* Table */}
      <div className="pe-table-container">
        {loading ? (
          <div className="pe-loading">Loading...</div>
        ) : logs.length === 0 ? (
          <div className="pe-empty">
            <span className="material-symbols-outlined">inbox</span>
            <p>No payment error logs found</p>
          </div>
        ) : (
          <table className="pe-table">
            <thead>
              <tr>
                <th>Platform</th>
                <th>Type</th>
                <th>Stage</th>
                <th>Error</th>
                <th>Amount</th>
                <th>Device</th>
                <th>Version</th>
                <th>Status</th>
                <th>Date</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {logs.map((log) => (
                <tr key={log._id} className={log.crashData?.resolved ? "resolved" : ""}>
                  <td>
                    <span className={`pe-platform ${log.crashData?.platform}`}>
                      <span className="material-symbols-outlined">
                        {getPlatformIcon(log.crashData?.platform)}
                      </span>
                    </span>
                  </td>
                  <td>
                    <span
                      className="pe-payment-type"
                      style={{ backgroundColor: getPaymentTypeColor(log.metadata?.paymentType) }}
                    >
                      {getPaymentTypeLabel(log.metadata?.paymentType)}
                    </span>
                  </td>
                  <td>
                    <span className="pe-stage">
                      {getStageLabel(log.metadata?.stage)}
                    </span>
                  </td>
                  <td className="pe-error-cell">
                    <div className="pe-error-text">
                      {log.errorData?.errorMessage?.substring(0, 40) || "-"}
                      {log.errorData?.errorMessage?.length > 40 ? "..." : ""}
                    </div>
                  </td>
                  <td className="pe-amount">{formatAmount(log.metadata?.amount)}</td>
                  <td>
                    <div className="pe-device">
                      <span>{log.crashData?.deviceBrand}</span>
                      <small>{log.crashData?.deviceModel}</small>
                    </div>
                  </td>
                  <td>
                    <div className="pe-version">
                      <span>v{log.crashData?.appVersion}</span>
                      <small>({log.crashData?.buildNumber})</small>
                    </div>
                  </td>
                  <td>
                    {log.crashData?.resolved ? (
                      <span className="pe-status resolved">
                        <span className="material-symbols-outlined">check_circle</span>
                      </span>
                    ) : (
                      <span className="pe-status pending">
                        <span className="material-symbols-outlined">warning</span>
                      </span>
                    )}
                  </td>
                  <td className="pe-date">{formatDate(log.createdAt)}</td>
                  <td>
                    <button className="pe-view-btn" onClick={() => openDetailModal(log)}>
                      <span className="material-symbols-outlined">visibility</span>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {!loading && logs.length > 0 && (
        <div className="pe-pagination">
          <div className="pe-pagination-info">
            {(pagination.currentPage - 1) * pagination.limit + 1} -{" "}
            {Math.min(pagination.currentPage * pagination.limit, pagination.totalLogs)} of{" "}
            {pagination.totalLogs}
          </div>
          <div className="pe-pagination-controls">
            <button
              onClick={() => handlePageChange(1)}
              disabled={pagination.currentPage === 1}
            >
              <span className="material-symbols-outlined">first_page</span>
            </button>
            <button
              onClick={() => handlePageChange(pagination.currentPage - 1)}
              disabled={pagination.currentPage === 1}
            >
              <span className="material-symbols-outlined">chevron_left</span>
            </button>
            <span className="pe-current-page">
              {pagination.currentPage} / {pagination.totalPages}
            </span>
            <button
              onClick={() => handlePageChange(pagination.currentPage + 1)}
              disabled={pagination.currentPage === pagination.totalPages}
            >
              <span className="material-symbols-outlined">chevron_right</span>
            </button>
            <button
              onClick={() => handlePageChange(pagination.totalPages)}
              disabled={pagination.currentPage === pagination.totalPages}
            >
              <span className="material-symbols-outlined">last_page</span>
            </button>
          </div>
          <select
            value={filters.limit}
            onChange={(e) => handleFilterChange("limit", parseInt(e.target.value))}
          >
            <option value="25">25</option>
            <option value="50">50</option>
            <option value="100">100</option>
          </select>
        </div>
      )}

      {/* Detail Modal */}
      {showDetailModal && selectedLog && (
        <div className="pe-modal-overlay" onClick={closeDetailModal}>
          <div className="pe-modal" onClick={(e) => e.stopPropagation()}>
            <div className="pe-modal-header">
              <h3>Payment Error Details</h3>
              <button onClick={closeDetailModal}>
                <span className="material-symbols-outlined">close</span>
              </button>
            </div>
            <div className="pe-modal-body">
              {/* Status Bar */}
              <div className={`pe-detail-status ${selectedLog.crashData?.resolved ? "resolved" : "pending"}`}>
                <span className="material-symbols-outlined">
                  {selectedLog.crashData?.resolved ? "check_circle" : "warning"}
                </span>
                <span>
                  {selectedLog.crashData?.resolved ? "Resolved" : "Unresolved Payment Error"}
                </span>
              </div>

              {/* Payment Info */}
              <div className="pe-detail-section pe-payment-section">
                <h4>Payment Information</h4>
                <div className="pe-detail-grid">
                  <p><strong>Payment Type:</strong> {getPaymentTypeLabel(selectedLog.metadata?.paymentType)}</p>
                  <p><strong>Stage:</strong> {getStageLabel(selectedLog.metadata?.stage)}</p>
                  <p><strong>Amount:</strong> {formatAmount(selectedLog.metadata?.amount)}</p>
                  <p><strong>Payment ID:</strong> {selectedLog.metadata?.paymentId || "-"}</p>
                  <p><strong>Transaction ID:</strong> {selectedLog.metadata?.transactionId || "-"}</p>
                  <p><strong>User ID:</strong> {selectedLog.userId || "anonymous"}</p>
                </div>
              </div>

              {/* Basic Info */}
              <div className="pe-detail-section">
                <h4>Basic Information</h4>
                <div className="pe-detail-grid">
                  <p><strong>Platform:</strong> {selectedLog.crashData?.platform}</p>
                  <p><strong>Endpoint:</strong> {selectedLog.errorData?.endpoint || "-"}</p>
                  <p><strong>Status Code:</strong> {selectedLog.errorData?.statusCode || "-"}</p>
                  <p><strong>Date:</strong> {formatDate(selectedLog.createdAt)}</p>
                </div>
              </div>

              {/* Device Info */}
              <div className="pe-detail-section">
                <h4>Device Information</h4>
                <div className="pe-detail-grid">
                  <p><strong>Device:</strong> {selectedLog.crashData?.deviceBrand} {selectedLog.crashData?.deviceModel}</p>
                  <p><strong>OS Version:</strong> {selectedLog.crashData?.osVersion}</p>
                  <p><strong>App Version:</strong> v{selectedLog.crashData?.appVersion} ({selectedLog.crashData?.buildNumber})</p>
                  <p><strong>Emulator:</strong> {selectedLog.crashData?.isEmulator ? "Yes" : "No"}</p>
                </div>
              </div>

              {/* Error Message */}
              <div className="pe-detail-section pe-error-section">
                <h4>Error Message</h4>
                <pre className="pe-error-message-full">{selectedLog.errorData?.errorMessage || "No error message"}</pre>
              </div>

              {/* Stack Trace */}
              {selectedLog.errorData?.errorStack && (
                <div className="pe-detail-section">
                  <details open>
                    <summary>Stack Trace</summary>
                    <pre className="pe-stack-trace">{selectedLog.errorData.errorStack}</pre>
                  </details>
                </div>
              )}

              {/* bKash Response */}
              {selectedLog.metadata?.bkashResponse && (
                <div className="pe-detail-section">
                  <details>
                    <summary>bKash Response</summary>
                    <pre>{JSON.stringify(selectedLog.metadata.bkashResponse, null, 2)}</pre>
                  </details>
                </div>
              )}

              {/* Full Metadata */}
              {selectedLog.metadata && Object.keys(selectedLog.metadata).length > 0 && (
                <div className="pe-detail-section">
                  <details>
                    <summary>Full Metadata</summary>
                    <pre>{JSON.stringify(selectedLog.metadata, null, 2)}</pre>
                  </details>
                </div>
              )}

              {/* Resolution Notes */}
              {selectedLog.crashData?.resolved && selectedLog.crashData?.resolutionNotes && (
                <div className="pe-detail-section pe-resolved-section">
                  <h4>Resolution Notes</h4>
                  <p>{selectedLog.crashData.resolutionNotes}</p>
                  <small>Resolved at: {formatDate(selectedLog.crashData.resolvedAt)}</small>
                </div>
              )}

              {/* Resolve Form */}
              {!selectedLog.crashData?.resolved && (
                <div className="pe-detail-section pe-resolve-section">
                  <h4>Mark as Resolved</h4>
                  <textarea
                    value={resolveNotes}
                    onChange={(e) => setResolveNotes(e.target.value)}
                    placeholder="Add resolution notes (optional)..."
                    rows={3}
                  />
                  <button
                    className="pe-resolve-btn"
                    onClick={handleResolve}
                    disabled={resolving}
                  >
                    {resolving ? (
                      "Resolving..."
                    ) : (
                      <>
                        <span className="material-symbols-outlined">check_circle</span>
                        Mark as Resolved
                      </>
                    )}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default PaymentErrors;
